import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Phone } from "lucide-react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { NAV } from "@/constants/testIds";

const links = [
  { id: "home", label: "Home", to: "/" },
  { id: "safety", label: "Safety", to: "/safety" },
  { id: "about", label: "About", to: "/about" },
  { id: "contact", label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // Always show the chrome background on inner pages (avoids transparent nav
  // over a non-hero top section).
  const isHome = location.pathname === "/";
  const showChrome = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when navigating
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showChrome
          ? "bg-white/90 backdrop-blur-xl border-b border-[#E2E8F0]/80 shadow-[0_1px_0_rgba(0,32,80,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-[76px]">
          <Link
            to="/"
            data-testid={NAV.logo}
            className="flex items-center gap-2.5 group"
          >
            <img
              src="/freightxchange-logo.png"
              alt="FreightXchange — Freight Brokers, Southern Africa"
              className="h-9 md:h-10 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                end={l.to === "/"}
                data-testid={NAV.link(l.id)}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-[14.5px] font-medium rounded-lg transition-colors ${
                    isActive
                      ? "text-[#002050]"
                      : "text-[#1F2937]/75 hover:text-[#002050] hover:bg-[#F1F5F9]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-active-underline"
                        className="absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-[#0070C0]"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+27110000000"
              className="hidden xl:inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#002050] hover:text-[#0070C0] transition-colors"
            >
              <Phone className="h-4 w-4 text-[#0070C0]" />
              +27 11 000 0000
            </a>
            <Link
              to="/contact"
              data-testid={NAV.ctaDemo}
              className="group inline-flex items-center gap-2 bg-[#002050] hover:bg-[#0070C0] text-white px-5 py-2.5 rounded-xl text-[14px] font-bold transition-all duration-300 shadow-[0_4px_14px_rgba(0,32,80,0.25)] hover:shadow-[0_8px_24px_rgba(0,112,192,0.35)] hover:-translate-y-0.5"
            >
              Get a Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <button
            type="button"
            data-testid={NAV.mobileToggle}
            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-lg border border-[#E2E8F0] bg-white"
            onClick={() => setOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid={NAV.mobileMenu}
            className="lg:hidden overflow-hidden bg-white border-t border-[#E2E8F0]"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.id}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `px-3 py-3 text-[16px] font-medium rounded-lg ${
                      isActive
                        ? "bg-[#0070C0]/10 text-[#002050]"
                        : "text-[#1F2937] hover:bg-[#F1F5F9]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-[#002050] text-white px-5 py-3 rounded-xl text-[15px] font-bold"
              >
                Get a Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
